/**
 * `AmbientScribeNote` — one generated note per model run (SN-19067).
 *
 * Read-only resource behind `GET /ai/ambient-scribes/notes/`. Contract:
 * `docs/ambient-scribes/frontend-integration-sn-19067.md` §3.2 / §7.
 *
 * Chart notes and audiology report summaries share this table; `parent_type` is what
 * distinguishes them.
 */

/**
 * Which kind of clinical artifact a note was generated for. Always set, even on failures.
 *
 * The audiology value was renamed `Report` → `AudiologyReport` backend-side and existing rows
 * were migrated, so it now matches `AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT`. Sending the
 * old value returns zero rows rather than an error (an unknown `parent_type` matches nothing),
 * which is why a stale frontend reads as "nothing generated yet" instead of failing loudly.
 */
export const AMBIENT_SCRIBE_NOTE_PARENT_TYPE = {
  CHART_NOTE: "ChartNote",
  AUDIOLOGY_REPORT: "AudiologyReport",
  /** The letter itself is the artifact, so `parent_id` is always null for this kind. */
  AFTER_VISIT_SUMMARY: "AfterVisitSummary"
} as const;

export type AmbientScribeNoteParentType =
  (typeof AMBIENT_SCRIBE_NOTE_PARENT_TYPE)[keyof typeof AMBIENT_SCRIBE_NOTE_PARENT_TYPE];

export const AMBIENT_SCRIBE_NOTE_STATUS = {
  SUCCEEDED: "succeeded",
  FAILED: "failed"
} as const;

export type AmbientScribeNoteStatus =
  (typeof AMBIENT_SCRIBE_NOTE_STATUS)[keyof typeof AMBIENT_SCRIBE_NOTE_STATUS];

/**
 * `initial` is the first generation for this scribe + template + parent kind; `regenerate` is any
 * later one for the same tuple.
 *
 * There is no caching: every generation calls the model, so triggering it twice with an identical
 * prompt produces two rows with distinct content rather than reusing the first. (A third value,
 * `cached`, existed in an earlier contract and was removed.)
 */
export type AmbientScribeNoteRunType = "initial" | "regenerate";

/**
 * Three different shapes reach the frontend — every field is optional because none of them is
 * present on all three. Read it through `getScribeNoteErrorDisplay`, never field-by-field.
 *
 * - Audiology summaries: `{code, message}`. `_fail()` in `ai/services/ambient_scribe_note_generation.py`
 *   picks the code from a fixed set, which is what `MESSAGES_BY_CODE` maps.
 * - SOAP, provider error parsed: `{provider_message, provider_error_type}` — the `metadata` dict
 *   built by `_apply_provider_error_details` in `ai/views/tasks.py`. **No `code`, no `message`.**
 * - SOAP, provider error opaque: `{message}` — the `str(error)[:512]` fallback in
 *   `_run_soap_llm_with_note_record`. Also no `code`.
 *
 * So a SOAP failure never carries a `code` and may not carry a `message`. Declaring either as
 * required (as this type used to) makes the compiler vouch for fields that are routinely absent.
 */
export type AmbientScribeNoteError = {
  /** Audiology path only. */
  code?: string;
  /** Audiology path, and the SOAP fallback. */
  message?: string;
  /** SOAP path when the provider error parsed into a dict. */
  provider_message?: string;
  provider_error_type?: string;
};

export interface AmbientScribeNote {
  id: number;
  ambient_scribe_id: number;
  /** `null` when the built-in prompt was used instead of a saved template. */
  ambient_scribe_template_id: number | null;
  patient_id: number | null;
  practice_id: number | null;
  staff_id: number | null;
  /** Generated note as HTML. `""` when `status: "failed"`. */
  notes: string;
  parent_type: AmbientScribeNoteParentType;
  /**
   * The artifact's id — the audiology report for a summary, the chart note for a SOAP note.
   *
   * Now set on failed runs too, so it is safe to filter by. Still typed nullable: a run that
   * fails before any artifact is resolvable has nothing to point at. Read `parent_type` to know
   * the KIND; a null `parent_id` never means "unknown kind".
   */
  parent_id: number | null;
  /**
   * The email this note was actually delivered in, or `null` if it never was.
   *
   * Optional because it only arrives when named in a `?query=` projection. Read it to know whether
   * a letter has been emailed, and pass it to `useGetEmailQuery` to preview what went out — the
   * stored `Email.html` is the delivered body, so it reflects any edit the sender made in the
   * compose modal rather than the generated draft.
   *
   * Per-NOTE, not per-recording: a Regenerate writes a new note with this unset. "Did this patient
   * get their summary?" is a rollup across the scribe's notes — see
   * `usePatientAfterVisitSummaries`.
   */
  sent_email_id?: number | null;
  run_type: AmbientScribeNoteRunType;
  status: AmbientScribeNoteStatus;
  prompt_version: string;
  /**
   * The fully resolved prompt sent to the model, for provenance and debugging.
   *
   * Optional because it is **only** returned when named in a `?query=` projection — it embeds
   * the whole transcript and can run to many KB, so it must never ride along on a list read.
   * Request it via `AMBIENT_SCRIBE_NOTE_QUERY_WITH_PROMPT` on a single-row fetch.
   */
  prompt?: string | null;
  /** How long the model call took. `null` on failure. */
  duration_ms: number | null;
  error: AmbientScribeNoteError | null;
  created_at: string;
  updated_at: string;
}

export interface AmbientScribeNoteFilters {
  patient?: number;
  ambient_scribe?: number;
  scribe_template?: number;
  staff?: number;
  /** Comma-separated list accepted, e.g. `"failed,succeeded"`. */
  status?: string;
  scribe_template_type?: string;
  /** Comma-separated list accepted, e.g. `"AudiologyReport,ChartNote"`. */
  parent_type?: string;
  /**
   * The artifact to scope to. Safe to send now that the backend sets `parent_id` on failed runs
   * as well as successful ones — pair it with `parent_type` and `limit: 1` to read the newest
   * attempt for one artifact (`useLatestScribeNote`).
   */
  parent_id?: number;
  limit?: number;
  offset?: number;
  /** `-pk` (default), `pk`, `-created_at`, `created_at`. Sort by pk for stable paging. */
  ordering?: string;
  /**
   * RestQL field projection. An inclusion list **replaces** the default payload, so it must
   * name every field the consumer renders. Use the `AMBIENT_SCRIBE_NOTE_QUERY*` constants in
   * `services/ambient-scribe.ts` rather than hand-writing one.
   */
  query?: string;
}
